import { Locale } from "@divespots/contracts";

/**
 * Cookieless Umami page-view tracking (see docker/umami/README.md). Off unless both build args are
 * set, and always off in the e2e builds. Umami follows SPA pushState navigation itself.
 */

/** Global the tracker calls as `window[name](type, payload)`; a falsy return drops the event. */
export const BEFORE_SEND_NAME = "divespotsUmamiBeforeSend";

/**
 * First path segments (after an optional locale) that are never tracked: signed-in-only pages
 * (`admin`, `konto` = account, `dive` = dive mode beta) and sign-in / token pages (`login`, plus the
 * auth callback names a magic link may land on).
 */
export const UNTRACKED_SEGMENTS: readonly string[] = [
  "admin",
  "konto",
  "account",
  "settings",
  "dive",
  "login",
  "auth",
  "magic-link",
  "verify",
  "api",
];

export function isTrackedPath(pathname: string): boolean {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length > 0 && Locale.safeParse(parts[0]).success) parts.shift();
  return !(parts.length > 0 && UNTRACKED_SEGMENTS.includes(parts[0]!.toLowerCase()));
}

function pathOf(url: unknown): string {
  if (typeof url !== "string") return "/";
  try {
    return new URL(url, "https://divespots.ch").pathname;
  } catch {
    return "/";
  }
}

interface UmamiPayload {
  url?: unknown;
  [key: string]: unknown;
}

export function beforeSend(_type: string, payload: UmamiPayload): UmamiPayload | false {
  return isTrackedPath(pathOf(payload?.url)) ? payload : false;
}

type EventData = Record<string, string | number | boolean>;

interface UmamiGlobal {
  umami?: { track?: (name: string, data?: EventData) => void };
}

const umamiOf = (): UmamiGlobal["umami"] =>
  typeof window === "undefined" ? undefined : (window as unknown as UmamiGlobal).umami;

/** True while the injected script is still loading; events fired meanwhile wait in `queue`. */
let loading = false;
let queue: Array<[string, EventData]> = [];

function send(name: string, data: EventData): void {
  try {
    umamiOf()?.track?.(name, data);
  } catch {
    // analytics never breaks the app
  }
}

/** Custom event; a no-op without the tracker (no env, e2e, ad blocker), held until it loads. */
export function trackEvent(name: string, data: EventData): void {
  if (umamiOf()?.track) send(name, data);
  else if (loading) queue.push([name, data]);
}

interface AnalyticsEnv {
  VITE_UMAMI_SRC?: string;
  VITE_UMAMI_WEBSITE_ID?: string;
  VITE_E2E?: string;
}

export function installAnalytics(
  env: AnalyticsEnv = import.meta.env,
  doc: Document = document,
): void {
  const src = env.VITE_UMAMI_SRC?.trim();
  const websiteId = env.VITE_UMAMI_WEBSITE_ID?.trim();
  if (!src || !websiteId || env.VITE_E2E) return;
  if (doc.querySelector("script[data-website-id]")) return;
  const win = doc.defaultView as (Window & Record<string, unknown>) | null;
  if (win) win[BEFORE_SEND_NAME] = beforeSend;
  const script = doc.createElement("script");
  script.defer = true;
  script.src = src;
  script.setAttribute("data-website-id", websiteId);
  script.setAttribute("data-domains", "divespots.ch");
  script.setAttribute("data-exclude-search", "true");
  script.setAttribute("data-exclude-hash", "true");
  script.setAttribute("data-do-not-track", "true");
  script.setAttribute("data-before-send", BEFORE_SEND_NAME);
  loading = true;
  script.addEventListener("load", () => {
    loading = false;
    const held = queue;
    queue = [];
    for (const [name, data] of held) send(name, data);
  });
  script.addEventListener("error", () => {
    loading = false;
    queue = [];
  });
  doc.head.appendChild(script);
}
